/**
 * A QR code, drawn as rectangles.
 *
 * One Konva shape for the whole grid rather than one per module: a code with a
 * URL in it is 25 modules square, which is 625 nodes, and Konva would then
 * hit-test every one of them on every pointer move.
 *
 * Modules are drawn at whole device pixels where possible. A QR code with
 * uneven module widths - which is what happens when 25 squares are divided into
 * 97 pixels - is measurably harder for a phone to read.
 */

import { Rect, Shape } from 'react-konva';
import type { QrLayer } from '../model/types';
import { mmToPx, toCss } from '../model/units';
import { grid } from '../codes/qr';

interface Props {
  layer: QrLayer;
  zoom: number;
}

export function QrNode({ layer, zoom }: Props) {
  const width = mmToPx(layer.w, zoom);
  const height = mmToPx(layer.h, zoom);
  const built = grid(layer);

  if (!built) {
    return <Empty width={width} height={height} />;
  }

  const fill = toCss(layer.fill);
  const background = toCss(layer.background);

  // Square, always, and centred if the box is not. A stretched QR code does not
  // scan, so the drawing refuses to stretch even when the box does.
  const side = Math.min(width, height);
  const offsetX = (width - side) / 2;
  const offsetY = (height - side) / 2;
  const module = side / built.count;

  return (
    <>
      <Rect width={width} height={height} fill={background} />

      <Shape
        width={width}
        height={height}
        sceneFunc={(context, shape) => {
          const canvas = context._context;

          canvas.save();
          canvas.fillStyle = fill;

          for (let row = 0; row < built.count; row++) {
            let run = 0;

            // Runs of dark modules become one rectangle. It halves the fill
            // calls on a typical code and, more usefully, removes the hairline
            // seams that appear between abutting rectangles at some zooms.
            for (let col = 0; col <= built.count; col++) {
              const dark = col < built.count && built.dark[row * built.count + col];

              if (dark) {
                run++;
                continue;
              }

              if (run > 0) {
                canvas.fillRect(
                  offsetX + (col - run) * module,
                  offsetY + row * module,
                  run * module,
                  module
                );

                run = 0;
              }
            }
          }

          canvas.restore();

          context.beginPath();
          context.rect(0, 0, shape.width(), shape.height());
          context.closePath();
          context.fillStrokeShape(shape);
        }}
        hitFunc={(context, shape) => {
          context.beginPath();
          context.rect(0, 0, shape.width(), shape.height());
          context.closePath();
          context.fillStrokeShape(shape);
        }}
      />
    </>
  );
}

/** A QR code with nothing in it yet, or with more than one will hold. */
function Empty({ width, height }: { width: number; height: number }) {
  return (
    <Rect
      width={width}
      height={height}
      fill="rgba(133, 136, 142, 0.14)"
      stroke="#6f737a"
      strokeWidth={1.25}
      dash={[5, 4]}
      strokeScaleEnabled={false}
    />
  );
}
